/**
 * Estado global de consentimiento de cookies compatible con Consent Mode v2
 * de Google. Almacena la elección en localStorage y envía las señales
 * `gtag('consent', 'update', ...)` cuando gtag está disponible.
 *
 * Categorías gestionadas:
 *  - necessary   → siempre activo (no configurable).
 *  - analytics   → analytics_storage
 *  - personalization → personalization_storage
 *  - marketing   → ad_storage + ad_user_data + ad_personalization
 */

const STORAGE_KEY = "asatta_cookie_consent_v1";
const OPEN_EVENT = "asatta:open-cookie-prefs";

export const DEFAULT_CONSENT = {
    necessary: true,
    analytics: false,
    personalization: false,
    marketing: false,
    // Marca temporal de la última decisión (ISO)
    decidedAt: null,
};

export function readConsent() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_CONSENT, ...parsed };
    } catch {
        return null;
    }
}

export function writeConsent(consent) {
    if (typeof window === "undefined") return;
    const next = {
        ...DEFAULT_CONSENT,
        ...consent,
        necessary: true,
        decidedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    pushConsentSignals(next);
    return next;
}

/**
 * Inicializa el estado por defecto de Consent Mode v2 (denegado) tan pronto
 * como sea posible en la carga de la página. Se llama una vez al iniciar la
 * app. Si el usuario ya había decidido, aplica su elección.
 */
export function initConsentDefaults() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
    };

    // Default: denegado hasta que el usuario decida.
    window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        personalization_storage: "denied",
        functionality_storage: "granted",
        security_storage: "granted",
        wait_for_update: 500,
    });

    const existing = readConsent();
    if (existing && existing.decidedAt) {
        pushConsentSignals(existing);
    }
}

function pushConsentSignals(consent) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("consent", "update", {
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
        analytics_storage: consent.analytics ? "granted" : "denied",
        personalization_storage: consent.personalization ? "granted" : "denied",
    });
}

export function acceptAll() {
    return writeConsent({
        necessary: true,
        analytics: true,
        personalization: true,
        marketing: true,
    });
}

export function rejectAll() {
    return writeConsent({
        necessary: true,
        analytics: false,
        personalization: false,
        marketing: false,
    });
}

/**
 * Dispara la apertura del panel de preferencias (usado desde Footer o desde
 * la Política de Cookies). El CookieBanner escucha este evento.
 */
export function openCookiePreferences() {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export const COOKIE_EVENTS = { OPEN: OPEN_EVENT };
